import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/** Data a page needs the shell's mobile nav bar to show instead of its default. */
export interface MobileNavBarOverride {
  title: ReactNode;
  /**
   * Three states, like `trailing`: omitting it (`undefined`) keeps the shell's
   * default section subtitle, `null` renders NONE (the users screen's Figma
   * frame is a title alone), and a node replaces it.
   */
  subtitle?: ReactNode | null;
  /** Replaces the default back-to-`/welcome` action, e.g. a category drill-down's "up". */
  onBack?: (() => void) | undefined;
  /**
   * Replaces the default account-menu avatar. `null` renders the trailing
   * slot empty (its 40px box is still reserved, so the title stays centered)
   * — the Menu screen's Figma frame carries no trailing action at all.
   */
  trailing?: ReactNode | null;
}

/**
 * Lets a mobile PAGE override the shell's nav bar for as long as it is
 * mounted — e.g. the Menu screen titles the bar "Меню" over the ACTIVE BRANCH
 * name (a live value with its own picker), where the default shell only knows
 * how to show the static sub-app/section pair from `eatsScreenForPath`.
 *
 * A context rather than a prop because the shell sits in `__root.tsx`, above
 * the routed page in the tree — the page cannot hand it a prop directly. This
 * mirrors the modal registry's shape (CLAUDE.md §4): the page declares INTENT
 * (`useMobileNavBarOverride({ title, subtitle })`), and the one place that
 * owns the chrome (the shell) decides how to render it.
 *
 * The context carries PLAIN DATA, not a rendered `MobileNavBar` element:
 * storing JSX in state would re-render the provider on every consumer render
 * (a fresh element is never `Object.is`-equal to the last one), where the
 * shell only needs to re-render when `title`/`subtitle` actually change.
 */
const MobileNavBarSlotContext = createContext<
  ((override: MobileNavBarOverride | null) => void) | null
>(null);

interface MobileNavBarSlotProviderProps {
  children: (override: MobileNavBarOverride | null) => ReactNode;
}

/**
 * Mounted once by `EatsShellLayoutMobile`. Hands the active page's override
 * (or `null`) to a render-prop, so the shell decides how to turn it into a
 * `MobileNavBar` — including its own default when nothing is registered.
 */
export function MobileNavBarSlotProvider({ children }: MobileNavBarSlotProviderProps) {
  const [override, setOverride] = useState<MobileNavBarOverride | null>(null);
  return (
    <MobileNavBarSlotContext.Provider value={setOverride}>
      {children(override)}
    </MobileNavBarSlotContext.Provider>
  );
}

/**
 * Registers `override` as the mobile nav bar's content for as long as the
 * calling component stays mounted; reverts to the shell's default on unmount.
 *
 * Re-registers whenever `title`/`subtitle`/`onBack` change (not on every
 * render) — the effect depends on the override's own fields rather than a
 * fresh object each time, so a parent re-render that doesn't change what the
 * bar shows doesn't churn the shell.
 *
 * No-ops outside `EatsShellLayoutMobile` (desktop, or a screen rendered
 * without the Eats shell) rather than throwing, so a shared page component
 * doesn't need to know which breakpoint mounted it.
 */
export function useMobileNavBarOverride(override: MobileNavBarOverride): void {
  const setOverride = useContext(MobileNavBarSlotContext);
  const { title, subtitle, onBack, trailing } = override;

  useEffect(() => {
    if (!setOverride) return;
    setOverride({ title, subtitle, onBack, trailing });
    return () => setOverride(null);
  }, [setOverride, title, subtitle, onBack, trailing]);
}
